import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '../../../components/ui/button';
import { Sparkles, Wand2, Save, Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';
import { texteSynthese } from '../../../components/bilan/SyntheseCandidatDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CP = [['atteint', 'Atteinte', 'bg-emerald-500', 'hover:bg-emerald-500'], ['partiel', 'Partielle', 'bg-amber-500', 'hover:bg-amber-500'], ['non_atteint', 'Non atteinte', 'bg-red-500', 'hover:bg-red-500']];

// Avis / décision proposés à partir des compétences pro saisies
export const proposerAvisDecision = (competences, cpIds, final) => {
  const vals = cpIds.map(id => competences?.[id]).filter(Boolean);
  if (!vals.length) return null;
  const rA = vals.filter(v => v === 'atteint').length / vals.length;
  const rNA = vals.filter(v => v === 'non_atteint').length / vals.length;
  if (rA >= 0.75) return { avis: 'favorable', decision: final ? 'valide' : 'poursuite' };
  if (rNA >= 0.5) return { avis: 'defavorable', decision: final ? 'non_valide' : 'vigilance' };
  return { avis: 'reserve', decision: final ? 'partiel' : 'vigilance' };
};

// Actions groupées du bilan : compétences pour tous, synthèses IA, pré-remplissage, enregistrement global.
export const BilanActionsGroupees = ({ dossierId, bilan, candidats, competencesPro, forms, setForms, onSaved }) => {
  const [iaProgress, setIaProgress] = useState(null);
  const [saving, setSaving] = useState(false);
  const evaluables = candidats.filter(c => !c.abandon);
  const final = bilan.type === 'final';

  const setAll = (fn) => setForms(prev => { const next = { ...prev }; evaluables.forEach(c => { next[c.candidat_id] = fn(prev[c.candidat_id] || {}, c); }); return next; });

  const appliquerCp = (cpId, v) => {
    setAll(f => ({ ...f, competences: { ...(f.competences || {}), [cpId]: v } }));
    toast.success(`« ${CP.find(x => x[0] === v)[1]} » appliquée à ${evaluables.length} candidat(s) — ajustable individuellement`);
  };
  const appliquerToutes = (v) => {
    setAll(f => ({ ...f, competences: Object.fromEntries(competencesPro.map(cp => [cp.id, v])) }));
    toast.success(`Toutes les compétences « ${CP.find(x => x[0] === v)[1]} » pour ${evaluables.length} candidat(s)`);
  };

  const preRemplir = () => {
    let n = 0;
    setAll(f => {
      const p = proposerAvisDecision(f.competences, competencesPro.map(cp => cp.id), final);
      if (!p) return f;
      const next = { ...f };
      if (!next.avis) { next.avis = p.avis; n++; }
      if (!next.decision) next.decision = p.decision;
      return next;
    });
    setTimeout(() => toast.success(n ? `Avis et décisions proposés pour ${n} candidat(s) (champs vides uniquement)` : 'Aucun champ vide à pré-remplir — saisissez d\'abord les compétences'), 0);
  };

  const genererSyntheses = async () => {
    const cibles = evaluables.filter(c => !(forms[c.candidat_id]?.synthese || '').trim());
    if (!cibles.length) { toast.info('Toutes les synthèses individuelles sont déjà renseignées'); return; }
    setIaProgress({ done: 0, total: cibles.length });
    let ok = 0;
    const sansDonnees = [];
    const erreurs = [];
    for (const c of cibles) {
      const base = `${API_URL}/dossiers/${dossierId}/candidats/${c.candidat_id}/synthese`;
      try {
        let r = await axios.get(base);
        if (!r.data?.synthese || r.data?.obsolete) r = await axios.post(`${base}/generer`);
        const txt = r.data?.synthese ? texteSynthese(r.data.synthese) : '';
        if (txt) { setForms(prev => ({ ...prev, [c.candidat_id]: { ...(prev[c.candidat_id] || {}), synthese: txt } })); ok++; }
      } catch (e) {
        const nom = `${c.nom} ${c.prenom}`.trim();
        if (e.response?.status === 400) sansDonnees.push(nom); else erreurs.push(`${nom} : ${e.response?.data?.detail || 'échec'}`);
      }
      setIaProgress(p => ({ ...p, done: p.done + 1 }));
    }
    setIaProgress(null);
    if (ok) toast.success(`${ok} synthèse(s) IA insérée(s) — relisez puis enregistrez`);
    if (sansDonnees.length) toast.info(`${sansDonnees.length} candidat(s) sans observation, alerte ni évaluation à synthétiser : ${sansDonnees.join(', ')}`, { duration: 8000 });
    erreurs.forEach(m => toast.error(m));
  };

  const toutEnregistrer = async () => {
    setSaving(true);
    try {
      const decisions = Object.fromEntries(candidats.map(c => { const f = forms[c.candidat_id] || {}; return [c.candidat_id, { competences: f.competences || {}, avis: f.avis || null, decision: f.decision || null, suite_parcours: f.suite_parcours || '', synthese: f.synthese || '', signataire_nom: f.signataire_nom || '' }]; }));
      const r = await axios.put(`${API_URL}/bilans/${dossierId}/${bilan.id}/decisions`, { decisions });
      toast.success(`${r.data.nb} décision(s) enregistrée(s)`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement'); } finally { setSaving(false); }
  };

  return (
    <div className="border-b bg-indigo-50/40 px-4 py-3 space-y-3" data-testid="bilan-actions-groupees">
      <div className="flex items-center gap-2 text-xs font-semibold text-indigo-700 uppercase tracking-wide"><Users className="w-3.5 h-3.5" /> Attribution générale — {evaluables.length} candidat(s) évaluable(s)</div>
      {competencesPro.length > 0 && (
        <div className="space-y-1" data-testid="bilan-cp-globales">
          <div className="flex items-center justify-between gap-2 text-xs bg-white/70 rounded-md px-2 py-1.5 border border-indigo-100">
            <span className="font-semibold text-slate-700">Toutes les compétences</span>
            <div className="flex gap-1">{CP.map(([v, l, bg]) => <button key={v} type="button" onClick={() => appliquerToutes(v)} className={`px-2 py-0.5 rounded text-[10px] text-white ${bg} hover:opacity-90`} data-testid={`bilan-cp-all-${v}`}>{l}</button>)}</div>
          </div>
          {competencesPro.map((cp, i) => (
            <div key={cp.id} className="flex items-center justify-between gap-2 text-xs px-2 py-1">
              <span className="text-slate-700 truncate">C{i + 1}. {cp.titre}</span>
              <div className="flex gap-1 shrink-0">{CP.map(([v, l, , hbg]) => <button key={v} type="button" onClick={() => appliquerCp(cp.id, v)} className={`px-1.5 py-0.5 rounded text-[10px] bg-white border text-slate-600 hover:text-white ${hbg} hover:border-transparent`} data-testid={`bilan-cp-global-${i}-${v}`}>{l}</button>)}</div>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" variant="outline" className="text-xs border-purple-200 text-purple-700 hover:bg-purple-50" disabled={!!iaProgress} onClick={genererSyntheses} data-testid="bilan-ia-tous">
          {iaProgress ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Synthèses IA {iaProgress.done}/{iaProgress.total}…</> : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Générer les synthèses IA de tous les candidats</>}
        </Button>
        <Button type="button" size="sm" variant="outline" className="text-xs" onClick={preRemplir} data-testid="bilan-preremplir"><Wand2 className="w-3.5 h-3.5 mr-1" /> Proposer avis & décisions</Button>
        <Button type="button" size="sm" className="text-xs bg-indigo-600 hover:bg-indigo-700 ml-auto" disabled={saving} onClick={toutEnregistrer} data-testid="bilan-save-all">{saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />} Tout enregistrer</Button>
      </div>
      <p className="text-[11px] text-slate-500">Les attributions générales pré-remplissent chaque candidat ; ajustez ensuite au cas par cas puis enregistrez. Les synthèses déjà rédigées ne sont jamais écrasées.</p>
    </div>
  );
};
